import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Clock, MapPin, Tag, MessageCircle, CheckCircle2, Loader2, ZoomIn, X, Share2, Copy, Facebook, Lock } from "lucide-react";
import { api, resolveImg, API } from "@/lib/api";
import { euDate } from "@/lib/utils";
import { RsvpConfirmedCard } from "@/components/site/RsvpConfirmedCard";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { RsvpForm } from "@/components/site/RsvpForm";
import { WhatsAppJoinButton, WhatsAppJoinStrip } from "@/components/site/WhatsAppJoin";
import { ChildProgramSignup } from "@/components/site/ChildProgramSignup";
import { RichText } from "@/components/site/RichText";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => { if (location.key && location.key !== "default") navigate(-1); else navigate("/events"); };
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, tl, lang } = useLang();
  const accessKey = searchParams.get("key") || "";
  const [paidRsvp, setPaidRsvp] = useState(null);
  const [payState, setPayState] = useState(null); // null | 'pending' | 'confirmed' | 'error'
  const [zoom, setZoom] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    const session = searchParams.get("rsvp_session");
    const paypalToken = searchParams.get("token"); // PayPal appends ?token=<order_id>&PayerID=...
    if (!session && !paypalToken) return;
    setPayState("pending");
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = paypalToken
          ? (await api.post(`/rsvps/paypal/capture/${paypalToken}`))
          : (await api.get(`/rsvps/status/${session}`));
        if (cancelled) return;
        if (data.rsvp_created || data.payment_status === "paid") {
          setPaidRsvp(data.rsvp || null);
          setPayState(data.rsvp_status === "approved" ? "approved" : "confirmed");
          toast.success(t("rsvp.paid_pending_toast"));
          searchParams.delete("rsvp_session");
          searchParams.delete("token");
          searchParams.delete("PayerID");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (data.payment_status === "expired" || tries >= 6) {
          setPayState("error");
          return;
        }
      } catch (e) {
        if (tries >= 6) { setPayState("error"); return; }
      }
      tries += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: event, isLoading, isError } = useQuery({
    queryKey: ["event", id, accessKey],
    queryFn: async () => (await api.get(`/events/${id}${accessKey ? `?key=${encodeURIComponent(accessKey)}` : ""}`)).data,
    retry: false,
  });

  // Always show the pretty slug in the address bar; old ID links redirect quietly.
  useEffect(() => {
    if (event && event.slug && id !== event.slug) {
      navigate(`/events/${event.slug}${window.location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, id]);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] text-[#475569]"><EditableText k="eventdetail.loading" as="span" /></div>;
  }
  if (isError || !event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F9F8F6] gap-4">
        <p className="font-serif text-2xl"><EditableText k="eventdetail.notfound" as="span" /></p>
        <Link to="/events" className="rounded-full bg-[#0F172A] text-white px-6 py-3 text-sm font-semibold"><EditableText k="eventdetail.backto" as="span" /></Link>
      </div>
    );
  }

  const img = resolveImg((event.flyer_i18n || {})[lang] || event.flyer_url || event.image_url);
  const prettyDate = new Date(event.date + "T00:00:00").toLocaleDateString(lang === "he" ? "he-IL" : "en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  if (event.locked) {
    const waNumber = (event.students_contact_number || t("contact.whatsapp") || "").replace(/[^0-9]/g, "");
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi! I'd like the access link for the students-only event "${event.title}".`)}`;
    const lockImg = resolveImg(event.image_url);
    return (
      <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="event-detail-locked">
        <div className="grain" />
        <Navbar />
        <div className="pt-28 sm:pt-32 pb-24">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <button onClick={goBack} data-testid="event-back"
              className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] transition-colors mb-8">
              <ArrowLeft size={16} /> <EditableText k="eventdetail.back" as="span" />
            </button>
            <div className="rounded-[2rem] overflow-hidden bg-white border border-[#0F172A]/5 shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
              {lockImg && (
                <div className="relative aspect-[16/9] bg-[#E8E6E1]">
                  <img src={lockImg} alt={tl(event, "title")} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#0F172A]/45 backdrop-blur-[2px]" />
                </div>
              )}
              <div className="p-8 sm:p-10 text-center">
                <span className="mx-auto w-16 h-16 rounded-full bg-[#BE923C]/12 flex items-center justify-center text-[#BE923C]"><Lock size={30} /></span>
                <h1 className="mt-5 font-serif text-3xl sm:text-4xl tracking-tight">{tl(event, "title")}</h1>
                <div className="mt-3 flex items-center justify-center gap-2 text-[#475569] text-sm">
                  <Calendar size={15} className="text-[#BE923C]" /> {prettyDate}
                </div>
                <div className="mt-6 rounded-2xl bg-[#F9F8F6] border border-[#0F172A]/5 p-6">
                  <h2 className="font-serif text-xl text-[#0F172A]"><EditableText k="eventdetail.students_title" as="span" /></h2>
                  <p className="mt-2 text-[#475569] leading-relaxed"><EditableText k="eventdetail.students_desc" as="span" /></p>
                  <a
                    data-testid="students-locked-whatsapp"
                    href={waUrl}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
                  >
                    <MessageCircle size={18} /> <EditableText k="eventdetail.students_wa" as="span" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const shareUrl = `${API}/e/${event.slug || event.id}`;
  const shareTitle = tl(event, "title");
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareTitle + " — " + shareUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const shareLabel = { en: "Share", cs: "Sdílet", he: "שיתוף", ru: "Поделиться" }[lang] || "Share";
  const copiedLabel = { en: "Link copied", cs: "Odkaz zkopírován", he: "הקישור הועתק", ru: "Ссылка скопирована" }[lang] || "Link copied";
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, url: shareUrl }); } catch (e) { /* cancelled */ }
      return;
    }
    setShareOpen((o) => !o);
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(copiedLabel); } catch (e) { /* ignore */ }
    setShareOpen(false);
  };
  const isPast = event.date < new Date().toISOString().slice(0, 10);
  const rsvpClosed = event.rsvp_closed && !isPast;

  const rsvpContent = event.use_child_signup ? (
    <div data-testid="rsvp-child-signup-block">
      <ChildProgramSignup compact defaultProgram={event.child_signup_program || "sunday"} returnPath={`/events/${event.slug || event.id}`} />
    </div>
  ) : event.no_reservation ? (
    <div data-testid="rsvp-no-reservation-block" className="rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] p-7 sm:p-8">
      <h3 className="font-serif text-2xl"><EditableText k="eventdetail.no_rsvp_title" as="span" /></h3>
      <p className="mt-2 text-[#475569]"><EditableText k="eventdetail.no_rsvp_desc" as="span" /></p>
      <a
        data-testid="no-reservation-whatsapp-btn"
        href={`https://wa.me/${((event.whatsapp_number || t("contact.whatsapp")) || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi! I'd like more info about "${event.title}" on ${euDate(event.date)}.`)}`}
        target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
      >
        <MessageCircle size={18} /> <EditableText k="eventdetail.no_rsvp_wa" as="span" />
      </a>
    </div>
  ) : event.rsvp_via_whatsapp ? (
    <div data-testid="rsvp-whatsapp-block" className="rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] p-7 sm:p-8">
      <h3 className="font-serif text-2xl"><EditableText k="eventdetail.rsvp_direct" as="span" /></h3>
      <p className="mt-2 text-[#475569]"><EditableText k="eventdetail.rsvp_direct_desc" as="span" /></p>
      <a
        data-testid="rsvp-whatsapp-btn"
        href={`https://wa.me/${(event.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi! I'd like to RSVP for "${event.title}" on ${euDate(event.date)}.`)}`}
        target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
      >
        <MessageCircle size={18} /> <EditableText k="eventdetail.rsvp_wa" as="span" />
      </a>
    </div>
  ) : (
    <RsvpForm event={event} accessKey={accessKey} />
  );

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="event-detail-page">
      <div className="grain" />
      <Navbar />

      <div className="pt-28 sm:pt-32 pb-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="flex items-center justify-between gap-3 mb-6">
            <button onClick={goBack} data-testid="event-back"
              className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] transition-colors">
              <ArrowLeft size={16} /> <EditableText k="eventdetail.back" as="span" />
            </button>
            <div className="relative">
              <button type="button" onClick={handleShare} data-testid="event-share-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/15 hover:border-[#BE923C] px-4 py-2 text-sm font-semibold transition-colors">
                <Share2 size={16} /> {shareLabel}
              </button>
              {shareOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShareOpen(false)} />
                  <div className="absolute end-0 mt-2 w-48 z-50 rounded-xl border border-[#0F172A]/10 bg-white shadow-xl overflow-hidden" data-testid="event-share-menu">
                    <a href={waUrl} target="_blank" rel="noopener noreferrer" onClick={() => setShareOpen(false)} data-testid="share-whatsapp"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-[#F9F8F6] transition-colors"><MessageCircle size={16} className="text-[#25D366]" /> WhatsApp</a>
                    <a href={fbUrl} target="_blank" rel="noopener noreferrer" onClick={() => setShareOpen(false)} data-testid="share-facebook"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-[#F9F8F6] transition-colors"><Facebook size={16} className="text-[#1877F2]" /> Facebook</a>
                    <button type="button" onClick={copyLink} data-testid="share-copy"
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-[#F9F8F6] transition-colors text-start"><Copy size={16} /> Copy link</button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Flyer */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
              className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.15)] bg-[#E8E6E1] self-start"
            >
              {img ? (
                <button type="button" onClick={() => setZoom(true)} data-testid="event-flyer-zoom" className="group/flyer block w-full cursor-zoom-in">
                  <img src={img} alt={tl(event, "title")} data-testid="event-flyer" className="w-full object-cover" />
                  <span className="absolute bottom-4 end-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white opacity-0 group-hover/flyer:opacity-100 transition-opacity">
                    <ZoomIn size={15} />
                  </span>
                </button>
              ) : (
                <div className="aspect-[4/5] flex items-center justify-center text-[#BE923C]/30 font-serif text-5xl">✡</div>
              )}
            </motion.div>

            {zoom && img && (
              <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" data-testid="event-flyer-lightbox" onClick={() => setZoom(false)}>
                <button type="button" data-testid="event-flyer-lightbox-close" onClick={() => setZoom(false)} className="absolute top-4 end-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
                  <X size={22} />
                </button>
                <img src={img} alt={tl(event, "title")} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
              </div>
            )}

            {/* Details */}
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.02]"
              >
                {tl(event, "title")}
              </motion.h1>

              <div className="mt-6 space-y-3">
                {[
                  { icon: Calendar, text: prettyDate, show: true },
                  { icon: Clock, text: tl(event, "time") || t("eventdetail.time_tba"), show: true },
                  { icon: MapPin, text: tl(event, "location") || t("eventdetail.default_loc"), show: !event.hide_location },
                ].filter((r) => r.show).map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3 text-[#0F172A]">
                    <span className="w-10 h-10 rounded-full bg-white border border-[#0F172A]/5 flex items-center justify-center text-[#BE923C]"><Icon size={17} /></span>
                    <span className="text-[15px]">{text}</span>
                  </div>
                ))}
                {event.hide_location && (
                  <div className="flex items-center gap-3 text-[#475569]">
                    <span className="w-10 h-10 rounded-full bg-white border border-[#0F172A]/5 flex items-center justify-center text-[#8C9B86]"><MapPin size={17} /></span>
                    <span className="text-[15px] italic"><EditableText k="eventdetail.loc_rsvp" as="span" /></span>
                  </div>
                )}
              </div>

              {tl(event, "description") && (
                <div className="mt-8">
                  <h2 className="font-serif text-2xl mb-3"><EditableText k="eventdetail.about" as="span" /></h2>
                  <RichText html={tl(event, "description")} testid="event-description" className="text-[#475569] leading-relaxed" />
                </div>
              )}

              <div className="mt-10">
                {isPast ? (
                  <div data-testid="rsvp-past-block" className="rounded-3xl bg-white border border-[#0F172A]/5 p-8 text-center">
                    <span className="mx-auto w-14 h-14 rounded-full bg-[#64748B]/12 flex items-center justify-center text-[#64748B]"><Calendar size={26} /></span>
                    <h3 className="mt-4 font-serif text-2xl"><EditableText k="eventdetail.past_title" as="span" /></h3>
                    <p className="mt-2 text-[#475569]"><EditableText k="eventdetail.past_desc" as="span" /></p>
                    <button onClick={() => navigate("/events")} className="mt-5 inline-flex items-center justify-center rounded-full bg-[#0F172A] text-white font-semibold px-7 py-3 hover:bg-[#1e293b] transition-colors"><EditableText k="eventdetail.back" as="span" /></button>
                  </div>
                ) : payState === "approved" ? (
                  <RsvpConfirmedCard event={event} rsvp={paidRsvp || { guests: 1, parts: [] }}><WhatsAppJoinButton /></RsvpConfirmedCard>
                ) : payState === "confirmed" ? (
                  <div data-testid="rsvp-paid-success" className="rounded-3xl bg-white border border-[#BE923C]/30 p-8 text-center">
                    <span className="mx-auto w-14 h-14 rounded-full bg-[#BE923C]/12 flex items-center justify-center text-[#BE923C]"><CheckCircle2 size={28} /></span>
                    <h3 className="mt-4 font-serif text-2xl"><EditableText k="rsvp.paid_pending_title" as="span" /></h3>
                    <p className="mt-2 text-[#475569]"><EditableText k="rsvp.paid_pending_desc" as="span" /></p>
                    <WhatsAppJoinButton />
                  </div>
                ) : payState === "pending" ? (
                  <div data-testid="rsvp-paying" className="rounded-3xl bg-white border border-[#0F172A]/5 p-8 text-center">
                    <Loader2 size={28} className="mx-auto animate-spin text-[#BE923C]" />
                    <p className="mt-4 text-[#475569]"><EditableText k="rsvp.paywait" as="span" /></p>
                  </div>
                ) : rsvpClosed ? (
                  <div data-testid="rsvp-closed-block" className="relative">
                    <div className="blur-[6px] pointer-events-none select-none opacity-70" aria-hidden="true">
                      {rsvpContent}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div className="rounded-3xl bg-white/95 backdrop-blur-sm border border-[#0F172A]/10 shadow-[0_20px_60px_rgba(15,23,42,0.12)] px-8 py-7 text-center max-w-sm">
                        <span className="mx-auto w-14 h-14 rounded-full bg-[#BE923C]/12 flex items-center justify-center text-[#BE923C]"><Lock size={26} /></span>
                        <h3 data-testid="rsvp-closed-title" className="mt-4 font-serif text-2xl text-[#0F172A]">{t(event.sold_out ? "eventdetail.sold_out_title" : "eventdetail.rsvp_closed_title")}</h3>
                      </div>
                    </div>
                  </div>
                ) : (
                  rsvpContent
                )}

                {event.wa_button_enabled && !isPast && !rsvpClosed && !payState && (event.wa_button_number || event.whatsapp_number || t("contact.whatsapp")) && (
                  <a
                    data-testid="event-custom-whatsapp-btn"
                    href={`https://wa.me/${((event.wa_button_number || event.whatsapp_number || t("contact.whatsapp")) || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi! I'd like to RSVP for "${event.title}" on ${euDate(event.date)}.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
                  >
                    <MessageCircle size={18} /> {tl(event, "wa_button_text")?.trim() || t("eventdetail.rsvp_wa")}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <WhatsAppJoinStrip />
      <Footer />
    </div>
  );
}
